import * as React from 'react';
import { toast as sonnerToast, Toaster as SonnerPrimitive } from 'sonner';

import { Alert } from './Alert';

// Toast is not a separate visual primitive — it's a *delivery
// mechanism* for Alert. `notify.*` renders an `<Alert>` inside Sonner's
// portal so the visual contract stays single-sourced.
//
//   notify.success('Saved changes')
//   notify.error('Request failed', { description: '...' })
//   notify.info('Heads up', { duration: 8000, position: 'top-right' })
//
// Supported options:
//   description  — supporting copy below the title row
//   duration     — auto-dismiss delay in ms (default: Sonner's ~4s)
//   position     — 'top-left' | 'top-right' | 'top-center' |
//                  'bottom-left' | 'bottom-right' | 'bottom-center'

const notifyOfType = (tone, title, options = {}) => {
    const { description, duration, position, ...toastOptions } = options;

    return sonnerToast.custom(
        id => (
            <Alert
                description={description}
                dismissible
                elevated
                onDismiss={() => sonnerToast.dismiss(id)}
                title={title}
                tone={tone}
            />
        ),
        {
            duration,
            position,
            ...toastOptions
        }
    );
};

const notify = {
    error: (title, options) => notifyOfType('destructive', title, options),
    info: (title, options) => notifyOfType('info', title, options),
    success: (title, options) => notifyOfType('success', title, options),
    warning: (title, options) => notifyOfType('warning', title, options)
};

// Mount once at the app root. Wraps Sonner's <Toaster> with WM defaults
// (unstyled — Alert paints — bottom-right, stacking with expand-on-hover,
// configurable visible count).
function ToastProvider({
    expand = false,
    maxVisible = 3,
    offset = 24,
    position = 'bottom-right',
    ...props
}) {
    return (
        <SonnerPrimitive
            theme="light"
            className="tw-toaster"
            expand={expand}
            closeButton={false}
            offset={offset}
            position={position}
            visibleToasts={maxVisible}
            toastOptions={{
                unstyled: true,
                classNames: {
                    toast: 'tw-w-full tw-max-w-[32rem] tw-shadow-none tw-bg-transparent tw-border-0 tw-p-0',
                    content: 'tw-w-full'
                }
            }}
            {...props}
        />
    );
}

export { notify, ToastProvider };
